import { supabase } from './supabase';
import {
  mapCourse,
  mapLesson,
  mapModule,
  type AcademyCourseDetail,
} from './academy';

/**
 * Academy code-based access.
 *
 * Learners never sign in with SSO. An admin issues a code (organization-wide or
 * scoped to a single course); the learner redeems it with their email and we
 * store a random access token in this browser. The token is the learner's
 * identity — re-entering the same code + email on any device returns the same
 * token and therefore the same progress.
 *
 * Nothing here is trusted on its own: the token is validated server-side by the
 * academy_* security-definer functions, which also re-check entitlement and
 * expiry before returning any gated content.
 */

export interface AcademyAccess {
  token: string;
  email: string;
  label: string;
  scope: string;
}

export interface AcademyLearnerState {
  email: string;
  label: string;
  scope: string;
  courseSlugs: string[];
}

export interface AcademyProgressRow {
  courseSlug: string;
  courseTitle: string;
  courseSummary: string;
  lessonId: string;
  lessonSlug: string;
  lessonTitle: string;
  completed: boolean;
  positionSeconds: number;
  updatedAt: string;
}

export type GatedCourseResult =
  | { status: 'ok'; course: AcademyCourseDetail }
  | { status: 'locked'; course: AcademyCourseDetail | null }
  | { status: 'not_found' };

const STORAGE_KEY = 'cendien_academy_access_v1';

// ── Local token storage ─────────────────────────────────────────────────────

export function getStoredAccess(): AcademyAccess | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AcademyAccess>;
    if (!parsed || typeof parsed.token !== 'string' || !parsed.token) return null;
    return {
      token: parsed.token,
      email: typeof parsed.email === 'string' ? parsed.email : '',
      label: typeof parsed.label === 'string' ? parsed.label : '',
      scope: typeof parsed.scope === 'string' ? parsed.scope : 'org',
    };
  } catch {
    return null;
  }
}

export function setStoredAccess(access: AcademyAccess | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (!access) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, JSON.stringify(access));
  } catch {
    /* storage unavailable — access stays in memory for the session only */
  }
}

// ── Redemption ──────────────────────────────────────────────────────────────

interface RedeemResponse {
  ok?: boolean;
  error?: string;
  token?: string;
  email?: string;
  label?: string;
  scope?: string;
}

export interface RedeemResult {
  ok: boolean;
  error?: string;
  access?: AcademyAccess;
}

export async function redeemAcademyCode(code: string, email: string): Promise<RedeemResult> {
  const { data, error } = await supabase.rpc('academy_redeem_code', {
    p_code: code.trim(),
    p_email: email.trim(),
  });
  if (error) return { ok: false, error: error.message };
  const res = (data || {}) as RedeemResponse;
  if (!res.ok || !res.token) {
    return { ok: false, error: res.error || 'That code could not be redeemed.' };
  }
  const access: AcademyAccess = {
    token: res.token,
    email: res.email || email.trim().toLowerCase(),
    label: res.label || '',
    scope: res.scope || 'org',
  };
  setStoredAccess(access);
  return { ok: true, access };
}

// ── Validation / reconciliation ─────────────────────────────────────────────

interface StateResponse {
  ok?: boolean;
  email?: string;
  label?: string;
  scope?: string;
  courseSlugs?: string[];
}

/**
 * Confirms a stored token is still valid and returns what it unlocks.
 *
 * Returns null only when the server positively says the token is invalid
 * (revoked / expired). A transport or server error THROWS instead, so callers
 * never wipe a learner's redeemed access because of a momentary hiccup.
 */
export async function fetchLearnerState(token: string): Promise<AcademyLearnerState | null> {
  if (!token) return null;
  const { data, error } = await supabase.rpc('academy_learner_state', { p_token: token });
  if (error) throw error;
  const res = (data || {}) as StateResponse;
  if (!res.ok) return null;
  return {
    email: res.email || '',
    label: res.label || '',
    scope: res.scope || 'org',
    courseSlugs: Array.isArray(res.courseSlugs) ? res.courseSlugs : [],
  };
}

export function courseIsUnlocked(state: AcademyLearnerState | null, slug: string): boolean {
  if (!state) return false;
  if (state.scope === 'org' || state.scope === 'catalog') return true;
  return state.courseSlugs.includes(slug);
}

// ── Gated course detail ─────────────────────────────────────────────────────

interface CourseTokenResponse {
  ok?: boolean;
  reason?: string;
  course?: Record<string, unknown>;
  modules?: Record<string, unknown>[];
  lessons?: Record<string, unknown>[];
}

/**
 * Loads a course (with modules + lessons) through the entitlement-verified
 * server function. Public courses come back for anyone; gated courses only
 * with a valid token.
 */
export async function fetchCourseWithToken(
  slug: string,
  token: string | null,
): Promise<GatedCourseResult> {
  const { data, error } = await supabase.rpc('academy_course_with_token', {
    p_slug: slug,
    p_token: token || '',
  });
  if (error) throw error;
  const res = (data || {}) as CourseTokenResponse;
  if (!res.ok) {
    if (res.reason === 'not_found') return { status: 'not_found' };
    const meta: AcademyCourseDetail | null = res.course
      ? { ...mapCourse(res.course as Record<string, unknown>), modules: [], lessons: [], lessonCount: 0 }
      : null;
    return { status: 'locked', course: meta };
  }
  const course = mapCourse((res.course || {}) as Record<string, unknown>);
  const lessons = (res.lessons || []).map((r) => mapLesson(r));
  const modules = (res.modules || [])
    .map((r) => mapModule(r, lessons))
    .sort((a, b) => a.position - b.position);
  const orphan = lessons.filter((l) => !l.moduleId).sort((a, b) => a.position - b.position);
  if (orphan.length) {
    modules.push({
      id: 'ungrouped',
      courseId: course.id,
      title: 'Lessons',
      summary: '',
      position: 9999,
      lessons: orphan,
    });
  }
  // Lesson `position` is only unique *within* a module, so the server's flat
  // list (ordered by position alone) interleaves modules and makes prev/next
  // jump across chapters. Rebuild the flat list from module order instead.
  const orderedLessons = modules.flatMap((m) => m.lessons);
  const detail: AcademyCourseDetail = {
    ...course,
    lessonCount: orderedLessons.length,
    modules,
    lessons: orderedLessons,
  };
  return { status: 'ok', course: detail };
}

// ── Progress ────────────────────────────────────────────────────────────────

export async function saveAcademyProgress(
  token: string,
  lessonId: string,
  completed: boolean,
  positionSeconds: number,
): Promise<boolean> {
  if (!token || !lessonId) return false;
  const { data, error } = await supabase.rpc('academy_save_progress', {
    p_token: token,
    p_lesson_id: lessonId,
    p_completed: completed,
    p_position: Math.max(0, Math.round(positionSeconds)),
  });
  if (error) return false;
  return Boolean((data as { ok?: boolean })?.ok);
}

interface ProgressResponse {
  ok?: boolean;
  progress?: Record<string, unknown>[];
}

export async function listAcademyProgress(token: string): Promise<AcademyProgressRow[]> {
  if (!token) return [];
  const { data, error } = await supabase.rpc('academy_list_progress', { p_token: token });
  if (error) throw error;
  const res = (data || {}) as ProgressResponse;
  if (!res.ok) return [];
  return (res.progress || []).map((r) => ({
    courseSlug: String(r.course_slug ?? ''),
    courseTitle: String(r.course_title ?? ''),
    courseSummary: String(r.course_summary ?? ''),
    lessonId: String(r.lesson_id ?? ''),
    lessonSlug: String(r.lesson_slug ?? ''),
    lessonTitle: String(r.lesson_title ?? ''),
    completed: Boolean(r.completed),
    positionSeconds: Number(r.position_seconds ?? 0),
    updatedAt: String(r.updated_at ?? ''),
  }));
}

// ── Code generation (admin) ─────────────────────────────────────────────────

/** Human-friendly, unambiguous code, e.g. CEND-7F3K-9QW2-4HK. */
export function generateAccessCode(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const block = (n: number) =>
    Array.from({ length: n }, () => alphabet[Math.floor(Math.random() * alphabet.length)]).join('');
  return `CEND-${block(4)}-${block(4)}-${block(3)}`;
}